export enum PageType {
  ABOUT_US = 'about_us',
  TERMS = 'terms',
  PRIVACY = 'privacy',
  ACCESSIBILITY = 'accessibility',
  FAQ = 'faq',
  ARTICLES = 'articles',
  LICENSING = 'licensing',
  COOKIE_CONSENT = 'cookie_consent',
}

import { ImageCaptionDTO, RawImageCaption } from './api.types';

export interface Article {
  id: string;
  title: string;
  summary: string;
  body: string;
  coverImageUrl: string | null;
  coverImage?: File | string | null;
  image_caption?: RawImageCaption;
  image_captions?: ImageCaptionDTO;
  media_captions?: ImageCaptionDTO[];
  coverImage_captions?: ImageCaptionDTO[];
  tags: string[];
  category: string;
  language: string;
  status: 'draft' | 'published' | 'archived';
  publishedAt: string | null;
  slug: string;
  createdAt: string;
  updatedAt: string;
  translations?: Record<string, ArticleTranslation>;
}

export interface ArticleVersion {
  version: number;
  createdAt: string;
  title: string;
  summary: string;
  body: string;
  coverImageUrl: string | null;
  category: string;
  language: string;
  status: 'draft' | 'published' | 'archived';
  publishedAt: string | null;
  slug: string;
  translations?: Record<string, ArticleTranslation>;
  publishedBy?: {
    id: string;
    email: string;
    name: string;
  };
}

export interface ArticleTranslation {
  title: string;
  summary: string;
  body: string;
  tags: string[];
}

export interface TranslateArticleRequest {
  title?: string;
  summary?: string;
  body?: string;
  tags?: string[];
  source_language: string;
}

export interface TranslateArticleResponse {
  status: boolean;
  message: string;
  translations: Record<string, ArticleTranslation>;
  warning_message?: string;
}

export interface Page {
  id?: string | null;
  pageType: PageType;
  status?: 'draft' | 'published';
  content?: string;
  createdAt?: string;
  updatedAt?: string;
  legal_version?: number;
  legal_effective_from?: string | null;
}

export interface sectionData {
  id: string;
  title: string;
  content: string;
  display_order: number;
}

// Language options
export const languageOptions = [
  { value: 'en', label: 'English' },
  { value: 'fi', label: 'Finnish' },
  { value: 'sv', label: 'Swedish' },
  { value: 'de', label: 'German' },
];

export interface aboutUsData {
  content: {
    heroBanner: {
      title: string;
      description: string;
    };
    vision: string;
    mission: string;
    values: string;
    footerDescription: string;
  };
}

export interface CMSFAQTranslationActor {
  id: string;
  name?: string | null;
  email?: string | null;
}

export interface CMSFAQTranslationState {
  status: 'draft' | 'published';
  title: string;
  content: string;
  is_auto_generated: boolean;
  updated_at: string | null;
  published_at?: string | null;
  updated_by?: CMSFAQTranslationActor | null;
  published_by?: CMSFAQTranslationActor | null;
}

export interface CMSFAQTranslationVariant {
  language: string;
  source_language: string;
  status: 'missing' | 'draft' | 'published';
  has_published_version: boolean;
  can_rollback: boolean;
  last_updated_at: string | null;
  last_updated_label?: string | null;
  last_updated_by?: CMSFAQTranslationActor | null;
  draft?: CMSFAQTranslationState | null;
  published?: CMSFAQTranslationState | null;
}

export interface CMSFAQItem {
  id: string;
  source_language: string;
  title: string;
  content: string;
  display_order: number;
  createdAt: string;
  updatedAt: string;
  translation_variants: CMSFAQTranslationVariant[];
}

export interface CMSFAQAdminPage {
  id?: string | null;
  pageType: PageType.FAQ;
  status?: 'draft' | 'published' | null;
  items: CMSFAQItem[];
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CreateCMSFAQItemRequest {
  source_language: string;
  title: string;
  content: string;
  display_order: number;
}

export interface UpdateCMSFAQItemRequest extends CreateCMSFAQItemRequest {
  id: string;
}

export interface GenerateCMSFAQDraftsRequest {
  id: string;
  target_languages: string[];
  source_language?: string;
  mode?: 'overwrite_draft' | 'create_new_draft_version';
}

export interface UpdateCMSFAQTranslationDraftRequest {
  id: string;
  language: string;
  title: string;
  content: string;
}

export interface PublishCMSFAQTranslationRequest {
  id: string;
  language: string;
}

export interface RollbackCMSFAQTranslationRequest {
  id: string;
  language: string;
}

// Helpdesk FAQ Types
export interface HelpdeskFAQ {
  id: string;
  title: string;
  body: string;
  is_helpful_count: string;
  is_not_helpful_count: string;
  is_deleted: boolean;
  language: string;
  translations: Record<string, HelpdeskFAQTranslation>;
  translation_variants?: HelpdeskFAQTranslationVariant[];
  created_at: string;
  updated_at: string;
}

export interface HelpdeskFAQTranslation {
  title: string;
  body: string;
}

export interface HelpdeskFAQTranslationActor {
  id: string;
  name?: string | null;
  email?: string | null;
}

export interface HelpdeskFAQTranslationState {
  status: 'draft' | 'published';
  title: string;
  body: string | null;
  is_auto_generated: boolean;
  updated_at: string | null;
  published_at?: string | null;
  updated_by?: HelpdeskFAQTranslationActor | null;
  published_by?: HelpdeskFAQTranslationActor | null;
}

export interface HelpdeskFAQTranslationVariant {
  language: string;
  source_language: string;
  status: 'missing' | 'draft' | 'published';
  has_published_version: boolean;
  can_rollback: boolean;
  last_updated_at: string | null;
  last_updated_label?: string | null;
  last_updated_by?: HelpdeskFAQTranslationActor | null;
  draft?: HelpdeskFAQTranslationState | null;
  published?: HelpdeskFAQTranslationState | null;
}

export interface TranslateHelpdeskFAQRequest {
  title?: string;
  body?: string;
  source_language: string;
}

export interface TranslateHelpdeskFAQResponse {
  status: boolean;
  message: string;
  translations: Record<string, HelpdeskFAQTranslation>;
}

export interface GenerateHelpdeskFAQDraftsRequest {
  id: string;
  target_languages: string[];
  source_language?: string;
  mode?: 'overwrite_draft' | 'create_new_draft_version';
}

export interface UpdateHelpdeskFAQTranslationDraftRequest {
  id: string;
  language: string;
  title: string;
  body: string;
}

export interface PublishHelpdeskFAQTranslationRequest {
  id: string;
  language: string;
}

export interface RollbackHelpdeskFAQTranslationRequest {
  id: string;
  language: string;
}
